import { useEffect, useState } from 'react';
import { api } from '../api';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

type Balances = Record<string, { value: number; decimal_places: number }>;

export default function Resources({ session }: Props) {
  const playerId = session.playerId;
  const [balances, setBalances] = useState<Balances>({});
  const [error, setError] = useState<string | null>(null);
  const [resourceId, setResourceId] = useState<string>('gold');
  const [amount, setAmount] = useState<number>(100);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    api
      .resourceWallet(session.gameId, 'player', playerId)
      .then(w => setBalances(w.state.balances))
      .catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId, playerId]);

  const action = async (kind: 'grant' | 'cost' | 'regen') => {
    setBusy(true); setError(null);
    try {
      if (kind === 'regen') {
        await api.resourceRegen(session.gameId, 'player', playerId, {});
      } else {
        const fn = kind === 'grant' ? api.resourceGrant : api.resourceCost;
        await fn(session.gameId, 'player', playerId, { resource_id: resourceId, amount });
      }
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const entries = Object.entries(balances);
  const shortPlayer = `${playerId.slice(0, 8)}…${playerId.slice(-4)}`;

  return (
    <Container>
      <PageHeader
        title="Resources"
        subtitle="Manage the selected player's wallet, grant/spend resources, and tick regen."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">
            Active player
          </div>
          <code className="text-fg" title={playerId}>{shortPlayer}</code>
        </div>
        <div className="text-right">
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">
            Resources held
          </div>
          <div className="text-2xl font-semibold tabular-nums">{entries.length}</div>
        </div>
      </div>

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        Wallet
      </h2>
      {entries.length === 0 ? (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">Empty wallet</div>
          <div className="text-xs text-fg-muted">
            Define a resource type in Config Explorer, publish, then grant below.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 mb-5">
          {entries.map(([key, q]) => (
            <div key={key} className="card hover:border-line-strong transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim">
                  {key}
                </div>
                <div className="badge badge-muted">prec {q.decimal_places}</div>
              </div>
              <div className="text-[28px] font-semibold tabular-nums tracking-tight">
                {q.value.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        Actions
      </h2>
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Resource ID
            </label>
            <input
              className="input"
              value={resourceId}
              onChange={e => setResourceId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Amount
            </label>
            <input
              className="input"
              type="number"
              value={amount}
              onChange={e => setAmount(parseInt(e.target.value, 10) || 0)}
              style={{ width: 120 }}
            />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={() => action('grant')}>
            Grant
          </button>
          <button className="btn" disabled={busy} onClick={() => action('cost')}>
            Apply cost
          </button>
          <button className="btn" disabled={busy} onClick={() => action('regen')}>
            Tick regen
          </button>
        </div>
        <div className="text-xs text-fg-muted mt-3">
          Resources must be defined in <strong>Config Explorer</strong> &amp; published before
          they can be granted.
        </div>
      </div>
    </Container>
  );
}
